import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { config, ENV_VARS } from 'ENV_CONFIG';
import { api } from '~/api';
import { LoadingSpinner } from '../loading-spinner';
import ESIInclude from 'jl-esi-components';
import styles from './with-config.hoc.scss';
import { inlineErrorMessagingQuestions } from '../questions';
import { envConfigActions } from './configuration.actions';
import { mapQuestion } from '~/components/questions/questions.actions';
import { FeaturesContext } from '~/components/features/with-features.hoc';

const getErrorMessagingQuestions = (loadedInlineErrorMessagingQuestions) =>
  inlineErrorMessagingQuestions.map((question) => {
    const loadedInlineErrorMessagingQuestion = loadedInlineErrorMessagingQuestions.find(
      (q) => q.name === question.name,
    );

    return {
      id: loadedInlineErrorMessagingQuestion
        ? loadedInlineErrorMessagingQuestion.id
        : 'NOT_CONFIGURED',
      ...question,
    };
  });

export const withConfig = (Content) =>
  class ComponentWithConfig extends PureComponent {
    static contextType = FeaturesContext;

    static propTypes = {
      dispatchSetEnvConfig: PropTypes.func.isRequired,
      appointmentTypeId: PropTypes.any,
    };

    state = {
      isLoaded: false,
    };

    static getDerivedStateFromProps(props) {
      if (!api.SERVICE_ID && props.appointmentTypeId) {
        api.SERVICE_ID = Object.values(config.services).find((service) =>
          Object.keys(service.appointmentTypes).find(
            (appointmentTypeId) => appointmentTypeId === props.appointmentTypeId,
          ),
        ).id;
      }
      return null;
    }

    componentDidMount() {
      const { dispatchSetEnvConfig } = this.props;

      api
        .fetchJSON('configs')
        .then(
          ({
            features,
            services,
            inlineErrorMessagingQuestions: loadedInlineErrorMessagingQuestions,
            howDidYouHearAboutOurServiceAnalyticsQuestions,
          }) => {
            const service = services[api.SERVICE_NAME];
            config.features = features;
            config.services = services;

            config.inlineErrorMessagingQuestions = getErrorMessagingQuestions(
              loadedInlineErrorMessagingQuestions,
            );
            config.excludedQuestionsIds = service && service.excludedQuestionsIds;
            api.SERVICE_ID = service && service.id;
            config.howDidYouHearAboutOurServiceAnalyticsQuestions =
              howDidYouHearAboutOurServiceAnalyticsQuestions.map((q) =>
                mapQuestion(q, {
                  treatDropdownQuestionAsChoice: false,
                  inlineErrorMessagingQuestions: config.inlineErrorMessagingQuestions,
                  analyticsQuestion: true,
                }),
              );
            dispatchSetEnvConfig(config);

            this.setState({
              isLoaded: true,
            });
          },
        )
        .catch((error) => {
          console.error('Failed to fetch configs:', error);
        });
    }

    render() {
      const { isLoaded } = this.state;
      const isAccount = window.location.hostname.startsWith('account');

      return (
        <>
          {!isAccount && (
            <>
              <ESIInclude
                id="esi-bootstrap"
                src={`/bootstrap/johnlewis/${ENV_VARS.ANALYTICS_ENV_ID}/bootstrap.html`}
              />
              <ESIInclude id="esi-banner" src="/cookies/banner.html" />
            </>
          )}

          <ESIInclude
            id="esi-browser-performance"
            src={`${ENV_VARS.REACT_APP_BROWSE_HOST}/browser-performance/metrics.html`}
          />

          <div className={styles.Config}>
            {isLoaded ? <Content {...this.props} /> : <LoadingSpinner centered />}
          </div>

          {isAccount ? (
            <ESIInclude
              id="esi-footer"
              src={`${ENV_VARS.REACT_APP_BROWSE_HOST}/esi/footer/accounts-no-chat?includeCoreCSS=false&includeCoreJS=false&includePolyfills=false`}
            />
          ) : (
            <ESIInclude
              id="esi-footer"
              src={`${ENV_VARS.REACT_APP_BROWSE_HOST}/esi/footer/default-no-chat`}
            />
          )}
        </>
      );
    }
  };

export const mapStateToProps = ({
  appointment: {
    draft: {
      appointmentType: { id },
    },
  },
}) => ({
  appointmentTypeId: id,
});

const mapDispatchToProps = {
  dispatchSetEnvConfig: envConfigActions.setEnvConfig,
};

export const ConnectedWithConfig = (Component) =>
  connect(mapStateToProps, mapDispatchToProps)(withConfig(Component));
